import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ElementRef,
  type ReactNode,
} from 'react';
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import { cn } from '@vitness/theme';

/**
 * Confirmation dialog built on Radix AlertDialog — the correct primitive for
 * destructive or otherwise irreversible actions. Unlike a plain Dialog, the
 * AlertDialog role moves initial focus to the cancel action and requires an
 * explicit choice (Escape maps to cancel). Token-driven and theme-aware.
 *
 * Use the convenience `ConfirmDialog` for the common title/description/confirm
 * + cancel shape, or compose from the exported primitive parts for custom UIs.
 */
export const ConfirmDialogRoot = AlertDialogPrimitive.Root;
export const ConfirmDialogTrigger = AlertDialogPrimitive.Trigger;
export const ConfirmDialogAction = AlertDialogPrimitive.Action;
export const ConfirmDialogCancel = AlertDialogPrimitive.Cancel;
export const ConfirmDialogPortal = AlertDialogPrimitive.Portal;

const ConfirmDialogOverlay = forwardRef<
  ElementRef<typeof AlertDialogPrimitive.Overlay>,
  ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Overlay>
>(function ConfirmDialogOverlay({ className, ...props }, ref) {
  return (
    <AlertDialogPrimitive.Overlay
      ref={ref}
      className={cn('fixed inset-0 z-50 bg-black/55 backdrop-blur-sm', className)}
      {...props}
    />
  );
});

export const ConfirmDialogContent = forwardRef<
  ElementRef<typeof AlertDialogPrimitive.Content>,
  ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Content>
>(function ConfirmDialogContent({ className, ...props }, ref) {
  return (
    <ConfirmDialogPortal>
      <ConfirmDialogOverlay />
      <AlertDialogPrimitive.Content
        ref={ref}
        className={cn(
          'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
          'w-full max-w-sm',
          'border border-border bg-surface-card text-text-primary',
          'rounded-vn-lg shadow-vn-card font-body p-vn-5 focus:outline-none',
          className,
        )}
        {...props}
      />
    </ConfirmDialogPortal>
  );
});

export const ConfirmDialogTitle = forwardRef<
  ElementRef<typeof AlertDialogPrimitive.Title>,
  ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Title>
>(function ConfirmDialogTitle({ className, ...props }, ref) {
  return (
    <AlertDialogPrimitive.Title
      ref={ref}
      className={cn(
        'font-display text-base font-semibold leading-tight text-text-primary',
        className,
      )}
      {...props}
    />
  );
});

export const ConfirmDialogDescription = forwardRef<
  ElementRef<typeof AlertDialogPrimitive.Description>,
  ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Description>
>(function ConfirmDialogDescription({ className, ...props }, ref) {
  return (
    <AlertDialogPrimitive.Description
      ref={ref}
      className={cn('mt-vn-1 text-sm leading-relaxed text-text-secondary', className)}
      {...props}
    />
  );
});

const ACTION_BASE = cn(
  'inline-flex items-center justify-center rounded-vn-md px-vn-3 py-vn-2',
  'text-sm font-semibold font-body transition-colors',
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1',
  'focus-visible:ring-offset-[var(--vn-surface-card)]',
);

export interface ConfirmDialogProps {
  /** Controlled open state. */
  open?: boolean;
  /** Open-state change handler (controlled usage). */
  onOpenChange?: (open: boolean) => void;
  /** Element that opens the dialog. Omit when driving `open` manually. */
  trigger?: ReactNode;
  /** Headline describing the action. */
  title: ReactNode;
  /** Supporting copy explaining consequences. */
  description?: ReactNode;
  /** Confirm button label. Defaults to `'Confirm'`. */
  confirmLabel?: string;
  /** Cancel button label. Defaults to `'Cancel'`. */
  cancelLabel?: string;
  /** Styles the confirm action as destructive (danger). Defaults to `false`. */
  destructive?: boolean;
  /** Invoked when the confirm action is chosen. */
  onConfirm?: () => void;
  /** Invoked when the dialog is dismissed via cancel/Escape/overlay. */
  onCancel?: () => void;
}

/**
 * Opinionated confirm dialog covering the common destructive-action flow.
 * Renders a labelled title + description and a cancel/confirm action pair, with
 * the confirm button styled as `danger` when `destructive` is set.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  trigger,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  return (
    <ConfirmDialogRoot open={open} onOpenChange={onOpenChange}>
      {trigger ? <ConfirmDialogTrigger asChild>{trigger}</ConfirmDialogTrigger> : null}
      <ConfirmDialogContent>
        <ConfirmDialogTitle>{title}</ConfirmDialogTitle>
        {description ? (
          <ConfirmDialogDescription>{description}</ConfirmDialogDescription>
        ) : null}
        <div className="mt-vn-5 flex flex-col-reverse gap-vn-2 sm:flex-row sm:justify-end">
          <ConfirmDialogCancel
            onClick={onCancel}
            className={cn(
              ACTION_BASE,
              'border border-border-strong bg-transparent text-text-primary',
              'hover:bg-surface-card-hover focus-visible:ring-[var(--vn-border-strong)]',
            )}
          >
            {cancelLabel}
          </ConfirmDialogCancel>
          <ConfirmDialogAction
            onClick={onConfirm}
            className={cn(
              ACTION_BASE,
              destructive
                ? 'bg-semantic-danger text-white hover:opacity-90 focus-visible:ring-semantic-danger'
                : 'bg-surface-card-hover text-text-primary hover:opacity-90 focus-visible:ring-[var(--vn-border-strong)]',
            )}
          >
            {confirmLabel}
          </ConfirmDialogAction>
        </div>
      </ConfirmDialogContent>
    </ConfirmDialogRoot>
  );
}
